import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { operatorConsole, OperatorTransaction, TransactionsResponse } from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = id ? Number(id) : 0;

  const [data, setData] = useState<TransactionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;

  useEffect(() => {
    if (sessionId === 0) return;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await operatorConsole.listTransactions({
          session_id: sessionId,
          page,
          per: PER_PAGE,
        });
        setData(res);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [sessionId, page]);

  const totalPages = data ? Math.ceil(data.meta.total_count / data.meta.per) : 0;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (sessionId === 0) return <div className="p-6">Invalid session ID</div>;
  if (loading) return <div className="p-6">Loading transactions...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;
  if (!data) return <div className="p-6">No data</div>;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-4">
        <Button
          onClick={() => navigate('/sessions')}
          className="px-3 py-1 text-sm"
        >
          ← Back
        </Button>
        <h1 className="text-2xl font-bold">Session #{sessionId}</h1>
      </div>

      {data.transactions.length === 0 ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          No transactions found
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-ui-border">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Direction</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Round</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Pool Order</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((tx: OperatorTransaction) => (
                  <tr
                    key={tx.id}
                    className="border-b border-ui-border hover:bg-ui-bg"
                  >
                    <td className="px-4 py-3 text-sm text-ui-text">{tx.id}</td>
                    <td className="px-4 py-3 text-sm text-ui-text capitalize">
                      {tx.direction}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text capitalize">
                      {tx.status}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text font-mono">
                      ${tx.amount_dollars}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text-secondary">
                      {tx.round_id || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text-secondary">
                      {tx.pool_order_id ? `#${tx.pool_order_id}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text-secondary">
                      {new Date(tx.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between rounded-lg border border-ui-border bg-ui-card px-4 py-3">
            <div className="text-sm text-ui-text-secondary">
              Page {page} of {totalPages} • Total {data.meta.total_count} transactions
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!canPrev}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Prev
              </Button>
              <Button
                onClick={() => setPage(p => p + 1)}
                disabled={!canNext}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
